import { callAll } from '@chakra-ui/utils'
import { omit } from 'lodash'
import React, { use, createContext, ElementType, useRef, useState } from 'react'
import ReactDatePicker, { DatePickerProps as ReactDatePickerProps } from 'react-datepicker'
import { createPortal } from 'react-dom'
import { Temporal } from 'temporal-polyfill'
import { useMsg } from '~/common/localizationUtils'
import { ClientOnly } from '~/common/nextJsUtils'
import { TOP_NAV_HEIGHT } from '~/common/scrollUtils'
import { Optional } from '~/common/types'
import { useRegisterLazyLoadedLocale } from '~/design_system/components/dates/useRegisterLazyLoadedLocale'
import { CalendarDay, ChevronLeft, ChevronRight, Watch } from '~/icons'
import {
  Box,
  Flex,
  forwardRef,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputProps,
  Skeleton,
  Stack,
  useControllableState,
  StackProps,
  useFormControlContext,
  useMergeRefs,
  VisuallyHidden,
  VisuallyHiddenInput
} from '../../chakra'
import { DateTimeRangeDisplay, TemporalInstance, useDateFormatters } from './DateDisplay'
import { container } from './DatePicker.style'

type ShowThisOrThatProps = 'showTimeSelect' | 'showTimeSelectOnly' | 'showMonthYearPicker'

type ValueAndOnChangeProps<T extends TemporalInstance> = {
  onChange: (temporalValue: T | null) => void
  selected?: T | null
}
type NonConfigurableDatePickerProps = Omit<
  BaseDatePickerProps,
  'selected' | 'onChange' | 'dateFormat' | '_dateFormat' | ShowThisOrThatProps
>

export const useDatePickerProps = () => {
  return {
    showTimeSelect: false,
    showTimeSelectOnly: false,
    showMonthYearPicker: false,
    // date-fns v3 formats: https://date-fns.org/v3.6.0/docs/format
    _dateFormat: [
      'PP',
      'P',
      'PPP',
      'MMM d yyyy',
      'MMM dd yyyy',
      'MMM d, yyyy',
      'MMM dd, yyyy',
      'MMMM d yyyy',
      'MMMM dd yyyy',
      'MMMM d, yyyy',
      'MMMM dd, yyyy',
      'd MMM yyyy',
      'dd MMM yyyy',
      'd MMM, yyyy',
      'dd MMM, yyyy',
      'd MMMM yyyy',
      'dd MMMM yyyy',
      'd MMMM, yyyy',
      'dd MMMM, yyyy',
      'yyyy-MM-dd',
      'yyyy/MM/dd',
      'yyyy-M-d',
      'yyyy/M/d',
      'MM/dd/yyyy',
      'MM/dd/yy',
      'M/d/yyyy',
      'M/d/yy',
      'dd-MM-yyyy',
      'dd-MM-yy',
      'd-M-yyyy',
      'd-M-yy'
    ]
  }
}

export const useTimePickerProps = () => {
  const msg = useMsg()
  return {
    inputIcon: Watch,
    timeIntervals: 15,
    timeCaption: msg('datepicker.time'),
    showTimeSelect: true,
    showTimeSelectOnly: true,
    showMonthYearPicker: false,
    // date-fns v3 formats: https://date-fns.org/v3.6.0/docs/format
    _dateFormat: ['p', 'pp', 'hh:mm a', 'HH:mm', 'hh a', 'h a']
  }
}

export const useDateTimePickerProps = () => {
  const msg = useMsg()
  return {
    timeIntervals: 15,
    timeCaption: msg('datepicker.time'),
    showTimeSelect: true,
    showTimeSelectOnly: false,
    showMonthYearPicker: false,
    // date-fns v3 formats: https://date-fns.org/v3.6.0/docs/format
    _dateFormat: [
      'PPp',
      'PP HH:mm',
      'PP hh:mm a',
      'yyyy-MM-dd HH:mm',
      'yyyy-MM-dd hh:mm a',
      'yyyy-MM-ddTHH:mm',
      'yyyy-MM-ddThh:mm a',
      'MM/dd/yyyy hh:mm a',
      'MM/dd/yyyy HH:mm',
      'yyyy/MM/dd hh:mm a',
      'yyyy/MM/dd HH:mm',
      'MM/dd/yy hh:mm a',
      'MM/dd/yy HH:mm',
      'dd-MM-yyyy HH:mm',
      'dd-MM-yyyy hh:mm a',
      'dd-MM-yy HH:mm',
      'dd-MM-yy hh:mm a',
      'MMM dd yyyy HH:mm',
      'MMM d yyyy HH:mm',
      'MMM dd yyyy hh:mm a',
      'MMM d yyyy hh:mm a',
      'MMM dd, yyyy HH:mm',
      'MMM d, yyyy HH:mm',
      'MMM dd, yyyy hh:mm a',
      'MMM d, yyyy hh:mm a',
      'MMMM dd yyyy HH:mm',
      'MMMM d yyyy HH:mm',
      'MMMM dd yyyy hh:mm a',
      'MMMM d yyyy hh:mm a',
      'MMMM dd, yyyy HH:mm',
      'MMMM d, yyyy HH:mm',
      'MMMM dd, yyyy hh:mm a',
      'MMMM d, yyyy hh:mm a',
      'dd MMM yyyy HH:mm',
      'd MMM yyyy HH:mm',
      'dd MMM yyyy hh:mm a',
      'd MMM yyyy hh:mm a',
      'dd MMM, yyyy HH:mm',
      'd MMM, yyyy HH:mm',
      'dd MMM, yyyy hh:mm a',
      'd MMM, yyyy hh:mm a',
      'dd MMMM yyyy HH:mm',
      'd MMMM yyyy HH:mm',
      'dd MMMM yyyy hh:mm a',
      'd MMMM yyyy hh:mm a',
      'dd MMMM, yyyy HH:mm',
      'd MMMM, yyyy HH:mm',
      'dd MMMM, yyyy hh:mm a',
      'd MMMM, yyyy hh:mm a'
    ]
  }
}

const useYearMonthPickerProps = () => {
  return {
    showTimeSelect: false,
    showTimeSelectOnly: false,
    showMonthYearPicker: true,
    // date-fns v3 formats: https://date-fns.org/v3.6.0/docs/format
    _dateFormat: [
      'MMM yyyy',
      'MMMM yyyy',
      'yyyy-MM',
      'MM-yyyy',
      'yyyy/MM',
      'MM/yyyy',
      'yyyy MM',
      'MM yyyy',
      'yyyy MMM',
      'yyyy MMMM'
    ]
  }
}

export type DatePickerProps = NonConfigurableDatePickerProps & ValueAndOnChangeProps<Temporal.PlainDate>

/**
 * DatePicker
 *
 * This component renders a date picker input field.
 * It allows users to select a date and emits a Temporal.PlainDate.
 *
 * @param {Temporal.PlainDate | Temporal.Instant | string | Date | null | undefined} selected - The currently selected date.
 * @param {(values: Temporal.PlainDate | null) => void} onChange - Callback function to handle date changes.
 *
 * @see: https://storybook.beta.vndly.com/?path=/docs/components-datepickers-timepickers--docs#datepicker
 */
export const DatePicker = forwardRef<DatePickerProps, 'input'>(({ selected, onChange, ...props }, ref) => {
  const dateFmt = useDateFormatters()
  return (
    <BaseDatePicker
      ref={ref}
      selected={dateFmt.toLegacyDate(selected)}
      onChange={(date: Date | null) => {
        if (date == null) return onChange?.(null)
        return onChange?.(dateFmt.fromLegacyDate.toPlainDate(date))
      }}
      {...useDatePickerProps()}
      {...props}
    />
  )
})

DatePicker.displayName = 'DatePicker'

export type DateTimePickerProps = NonConfigurableDatePickerProps & ValueAndOnChangeProps<Temporal.PlainDateTime>

/**
 * DateTimePicker
 *
 * This component renders a date + time picker input field.
 * It allows users to select a date and a time and emits a Temporal.PlainDateTime.
 *
 * @param {Temporal.PlainDateTime | Temporal.Instant | string | Date | null | undefined} selected - The currently selected date and time.
 * @param {(values: Temporal.PlainDateTime | null) => void} onChange - Callback function to handle date and time changes.
 *
 * @see: https://storybook.beta.vndly.com/?path=/docs/components-datepickers-timepickers--docs#timepicker
 */
export const DateTimePicker = forwardRef<DateTimePickerProps, 'input'>(({ selected, onChange, ...props }, ref) => {
  const dateFmt = useDateFormatters()
  return (
    <BaseDatePicker
      ref={ref}
      selected={dateFmt.toLegacyDate(selected)}
      onChange={(date: Date | null) => {
        if (date == null) return onChange?.(null)
        return onChange?.(dateFmt.fromLegacyDate.toPlainDateTime(date))
      }}
      {...useDateTimePickerProps()}
      {...props}
    />
  )
})

DateTimePicker.displayName = 'DateTimePicker'

export type TimePickerProps = NonConfigurableDatePickerProps & ValueAndOnChangeProps<Temporal.PlainTime>

/**
 * TimePicker
 *
 * This component renders a time-only picker input field.
 * It allows users to select a time and emits a Temporal.PlainTime.
 *
 * @param {Temporal.PlainTime | Temporal.Instant | string | Date | null | undefined} selected - The currently selected time.
 * @param {(values: Temporal.PlainTime | null) => void} onChange - Callback function to handle time changes.
 *
 * @see: https://storybook.beta.vndly.com/?path=/docs/components-datepickers-timepickers--docs#datetimepicker
 */
export const TimePicker = forwardRef<TimePickerProps, 'input'>(({ selected, onChange, ...props }, ref) => {
  const dateFmt = useDateFormatters()
  return (
    <BaseDatePicker
      ref={ref}
      selected={dateFmt.toLegacyDate(selected)}
      onChange={(date: Date | null) => {
        if (date == null) return onChange?.(null)
        return onChange?.(dateFmt.fromLegacyDate.toPlainTime(date))
      }}
      {...useTimePickerProps()}
      {...props}
    />
  )
})

TimePicker.displayName = 'TimePicker'

export type YearMonthPickerProps = Omit<NonConfigurableDatePickerProps, 'maxDate'> &
  ValueAndOnChangeProps<Temporal.PlainYearMonth> & { maxDate?: Temporal.PlainYearMonth }
/**
 * YearMonthPicker
 *
 * This component renders a year+month-only picker input field.
 * It allows users to select a year and a month and emits a Temporal.PlainYearMonth.
 *
 * @param {Temporal.PlainYearMonth | Temporal.Instant | string | Date | null | undefined} selected - The currently selected year and month.
 * @param {(values: Temporal.PlainYearMonth | null) => void} onChange - Callback function to handle year and month changes.
 *
 * @see: https://storybook.beta.vndly.com/?path=/docs/components-datepickers-timepickers--docs#yearmonthpicker
 */
export const YearMonthPicker = forwardRef<YearMonthPickerProps, 'input'>(
  (
    {
      selected,
      onChange,
      maxDate,
      'data-testid': dataTestId = 'year-month-picker',
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      ...props
    },
    ref
  ) => {
    const dateFmt = useDateFormatters()
    const msg = useMsg()
    const backOneMonth = () => selected && onChange(selected.subtract({ months: 1 }))
    const forwardOneMonth = () => selected && onChange(selected.add({ months: 1 }))
    const forwardDisabled =
      maxDate != null && selected != null && Temporal.PlainYearMonth.compare(selected, maxDate) >= 0
    const yearMonthLabel = dateFmt.formatYearMonth(selected)
    const inputHint = msg('datepicker.year_month_picker.input_hint', { yearMonth: yearMonthLabel })
    const hiddenInputLabel = ariaLabel ? `${ariaLabel}, ${inputHint}` : inputHint ?? undefined
    const groupLabel = ariaLabelledBy ? undefined : ariaLabel ?? msg('datepicker.year_month_picker.label')
    return (
      <Box width="fit-content" css={container}>
        <Flex
          className="weekly-picker"
          role="group"
          aria-labelledby={ariaLabelledBy}
          aria-label={groupLabel}
          minW={200}
          minH={10}
          justifyContent="space-between"
          border="1px solid"
          borderColor="var(--vndly-color-container-border)"
          borderRadius="base"
          alignItems="center">
          <IconButton
            size="xs"
            icon={<ChevronLeft />}
            onClick={backOneMonth}
            aria-label={msg('common.ui.previous_month')}
            variant="ghost"
            padding={1}
            h="full"
          />
          <BaseDatePicker
            ref={ref}
            data-testid={dataTestId}
            aria-label={hiddenInputLabel}
            selected={dateFmt.toLegacyDate(selected)}
            onChange={(date: Date | null) => {
              if (date == null) return onChange?.(null)
              return onChange?.(dateFmt.fromLegacyDate.toPlainYearMonth(date))
            }}
            inputProps={{
              // we want to avoid a border for the input because we're putting a
              //  border around the whole picker
              border: 'none',
              'aria-haspopup': 'dialog' as const
            }}
            {...useYearMonthPickerProps()}
            {...props}
          />
          <IconButton
            size="xs"
            icon={<ChevronRight />}
            onClick={forwardOneMonth}
            aria-label={msg('common.ui.next_month')}
            variant="ghost"
            padding={1}
            h="full"
            isDisabled={forwardDisabled}
          />
        </Flex>
        <VisuallyHidden aria-live="polite" aria-atomic="true">
          {yearMonthLabel}
        </VisuallyHidden>
      </Box>
    )
  }
)
YearMonthPicker.displayName = 'YearMonthPicker'

export type BaseDatePickerProps = Omit<
  ReactDatePickerProps,
  | 'showMonthYearDropdown'
  | 'showMonthDropdown'
  | 'showYearDropdown'
  | 'selectsRange'
  | 'selectsMultiple'
  | 'formatMultipleDates'
  | 'onChange'
> & {
  'data-testid'?: string
  inputProps?: InputProps
  inputIcon?: React.ElementType
  /**
   * Private prop to control date formatting.
   */
  _dateFormat?: ReactDatePickerProps['dateFormat']
  onChange?: (date: Date | null, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void
}

/**
 * Prevents react-datepicker's handleFocus from reopening the calendar
 * when we programmatically restore focus to the input after the calendar closes.
 *
 * Returns:
 * - `containerProps` — spread onto the container element wrapping ReactDatePicker
 * - `restoreFocus(inputRef)` — call inside `onCalendarClose` to safely refocus the input
 */
const useFixedReturnFocusBehavior = () => {
  const isRestoringFocusRef = useRef(false)

  const containerProps = {
    onFocusCapture: (e: React.FocusEvent) => {
      if (isRestoringFocusRef.current) {
        isRestoringFocusRef.current = false
        e.stopPropagation()
      }
    }
  }

  const restoreFocus = (inputRef: React.MutableRefObject<HTMLInputElement | undefined>) => {
    isRestoringFocusRef.current = true
    inputRef.current?.focus()
    isRestoringFocusRef.current = false
  }

  return { containerProps, restoreFocus }
}

export const BaseDatePicker = forwardRef<BaseDatePickerProps, 'input'>(
  (
    {
      _dateFormat,
      'aria-label': ariaLabel,
      'data-testid': dataTestId,
      withPortal = true,
      inputProps = {},
      inputIcon,
      onCalendarClose,
      onChange,
      ...reactDatePickerProps
    },
    outerRef
  ) => {
    const innerRef = useRef<HTMLInputElement>(undefined)
    const ref = useMergeRefs(outerRef, innerRef)
    const ctx = useFormControlContext()
    const isReadOnly = inputProps?.isReadOnly ?? ctx?.isReadOnly
    const { containerProps, restoreFocus } = useFixedReturnFocusBehavior()

    const { localeInfo } = useRegisterLazyLoadedLocale()
    return (
      <Box css={container} {...containerProps}>
        <Skeleton data-testid="muted-skeleton" isLoaded={!!localeInfo}>
          {!!localeInfo && (
            <ReactDatePicker
              dropdownMode="select"
              autoComplete="off"
              locale={localeInfo}
              showMonthDropdown
              showYearDropdown
              open={isReadOnly ? false : undefined}
              closeOnScroll={() => {
                // If top of popper is above the bottom of the header, then close the menu
                const popper = document.body.querySelector('.react-datepicker-popper')
                if (!popper) return false
                const top = popper.getBoundingClientRect().top
                return top < TOP_NAV_HEIGHT
              }}
              customInput={
                <DateInputWithIcon
                  ref={el => {
                    ref?.(el)
                  }}
                  data-testid={dataTestId}
                  aria-label={ariaLabel}
                  icon={inputIcon}
                  {...inputProps}
                />
              }
              onCalendarClose={() => {
                restoreFocus(innerRef)
                onCalendarClose?.()
              }}
              onChange={(
                date: Date | null,
                event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
              ) => {
                onChange?.(date, event)
              }}
              // Have to set this to allow the above `ref={ref}` to work
              // Using onDragOver to avoid getting an `invalid prop usage` error, it's an uncommon handler
              // and can accept the ref object without throwing an error.
              customInputRef="onDragOver"
              portalId={withPortal ? 'datepicker-portal' : undefined}
              showPopperArrow={false}
              dateFormat={_dateFormat}
              strictParsing
              {...omit(reactDatePickerProps, 'dateFormat')}
            />
          )}
          {withPortal && (
            <ClientOnly>{() => createPortal(<Box id="datepicker-portal" css={container} />, document.body)}</ClientOnly>
          )}
        </Skeleton>
      </Box>
    )
  }
)

BaseDatePicker.displayName = 'BaseDatePicker'

const DateInputWithIcon = forwardRef<InputProps & { icon?: ElementType; onIconClick?: () => void }, 'input'>(
  ({ icon: Icon, ...props }, ref) => {
    const ctx = useFormControlContext()
    const isDisabled = ctx?.isDisabled || props.isDisabled || props.disabled
    const isReadOnly = ctx?.isReadOnly || props.isReadOnly || props.readOnly
    const isRequired = ctx?.isRequired || props.isRequired || props.required
    return (
      <InputGroup>
        <InputLeftElement pointerEvents="none" opacity={isDisabled ? 0.4 : 1}>
          <Box as={Icon ?? CalendarDay} w={6} h={6} />
        </InputLeftElement>
        <Input
          {...omit(props, ['disabled', 'readOnly', 'required'])}
          isDisabled={isDisabled ? true : undefined}
          isReadOnly={isReadOnly ? true : undefined}
          isRequired={isRequired ? true : undefined}
          ref={ref}
        />
      </InputGroup>
    )
  }
)

DateInputWithIcon.displayName = 'DateInputWithIcon'

/// /////////////////
// DateRangePicker //
/// /////////////////

export type RangeObject<T> =
  | {
      start?: T | null
      end?: T | null
    }
  | undefined

export type DateRangeContextType<T> = {
  selected: RangeObject<T>
  setSelected: React.Dispatch<React.SetStateAction<RangeObject<T>>>
}

const DateRangeContext = createContext<DateRangeContextType<Temporal.PlainDate> | undefined>(undefined)

const useDateRangeContext = () => {
  const context = use(DateRangeContext)
  if (!context) {
    throw new Error('useDateRangeContext must be used within a DateRangeProvider')
  }
  return context
}

export type DateRangePickerRootProps = Omit<StackProps, 'onChange'> & {
  value?: RangeObject<Temporal.PlainDate>
  defaultValue?: RangeObject<Temporal.PlainDate>
  onChange?: (value: RangeObject<Temporal.PlainDate>) => void
}

/**
 * DateRangePicker
 *
 * @example
 * const [range, setRange] = useState<RangeObject<Temporal.PlainDate>>()
 *
 * <DateRangePicker.Root isInline value={range} onChange={setRange}>
 *   <FormControl>
 *     <FormLabel>Start Date</FormLabel>
 *     <DateRangePicker.StartDateInput />
 *   </FormControl>
 *   <FormControl>
 *     <FormLabel>End Date</FormLabel>
 *     <DateRangePicker.EndDateInput />
 *   </FormControl>
 * </DateRangePicker.Root>
 */
export const DateRangePicker = {
  Root: DateRangePickerRoot,
  StartDateInput,
  EndDateInput
}

function DateRangePickerRoot({ value, defaultValue, onChange, ...props }: DateRangePickerRootProps) {
  const [selected, setSelected] = useControllableState<RangeObject<Temporal.PlainDate>>({
    value,
    defaultValue,
    onChange
  })

  return (
    <DateRangeContext value={{ selected, setSelected }}>
      <Stack spacing="lg" {...props} />
    </DateRangeContext>
  )
}

DateRangePickerRoot.displayName = 'DateRangePicker.Root'

function StartDateInput({ onChange, ...props }: Optional<DatePickerProps, 'onChange'>) {
  const { selected, setSelected } = useDateRangeContext()

  const dateFmt = useDateFormatters()
  const startDate = dateFmt.toLegacyDate(selected?.start ?? undefined) ?? undefined
  const endDate = dateFmt.toLegacyDate(selected?.end ?? undefined) ?? undefined

  return (
    <DatePicker
      onChange={callAll(date => setSelected({ start: date, end: selected?.end }), onChange)}
      selected={selected?.start}
      selectsStart
      startDate={startDate}
      endDate={endDate}
      maxDate={endDate}
      {...props}
    />
  )
}

StartDateInput.displayName = 'DateRangePicker.StartDateInput'

function EndDateInput({ onChange, ...props }: Optional<DatePickerProps, 'onChange'>) {
  const { selected, setSelected } = useDateRangeContext()

  const dateFmt = useDateFormatters()
  const startDate = dateFmt.toLegacyDate(selected?.start ?? undefined) ?? undefined
  const endDate = dateFmt.toLegacyDate(selected?.end ?? undefined) ?? undefined

  return (
    <DatePicker
      onChange={callAll(end => setSelected({ start: selected?.start, end }), onChange)}
      selected={selected?.end}
      selectsEnd
      startDate={startDate}
      endDate={endDate}
      minDate={startDate}
      {...props}
    />
  )
}

EndDateInput.displayName = 'DateRangePicker.EndDateInput'

/// /////////////////
// TimeRangePicker //
/// /////////////////
const TimeRangeContext = createContext<DateRangeContextType<Temporal.PlainTime> | undefined>(undefined)

const useTimeRangeContext = () => {
  const context = use(TimeRangeContext)
  if (!context) {
    throw new Error('useTimRangeContext must be used within a TimRangeProvider')
  }
  return context
}

export type TimeRangePickerRootProps = Omit<StackProps, 'onChange'> & {
  value?: RangeObject<Temporal.PlainTime>
  defaultValue?: RangeObject<Temporal.PlainTime>
  onChange?: (value: RangeObject<Temporal.PlainTime>) => void
}

export const TimeRangePicker = {
  Root: TimeRangePickerRoot,
  StartTimeInput,
  EndTimeInput
}

/**
 * TimeRangePicker
 *
 * @example
 * const [range, setRange] = useState<RangeObject<Temporal.PlainTime>>()
 *
 * <TimeRangePicker.Root isInline value={range} onChange={setRange}>
 *   <FormControl>
 *     <FormLabel>Start Time</FormLabel>
 *     <TimeRangePicker.StartTimeInput />
 *   </FormControl>
 *   <FormControl>
 *     <FormLabel>End Time</FormLabel>
 *     <TimeRangePicker.EndTimeInput />
 *   </FormControl>
 * </TimeRangePicker.Root>
 */
function TimeRangePickerRoot({ value, defaultValue, onChange, ...props }: TimeRangePickerRootProps) {
  const [selected, setSelected] = useControllableState<RangeObject<Temporal.PlainTime>>({
    value,
    defaultValue,
    onChange
  })

  return (
    <TimeRangeContext value={{ selected, setSelected }}>
      <Stack spacing="lg" {...props} />
    </TimeRangeContext>
  )
}

TimeRangePickerRoot.displayName = 'TimeRangePicker.Root'

function StartTimeInput({ onChange, ...props }: Optional<TimePickerProps, 'onChange'>) {
  const { selected, setSelected } = useTimeRangeContext()

  const dateFmt = useDateFormatters()
  const endDate = dateFmt.toLegacyDate(selected?.end ?? undefined) ?? undefined
  const today = Temporal.Now.zonedDateTimeISO()
  const todayBeginning = new Date(today.startOfDay().epochMilliseconds)
  const todayEnd = new Date(today.with({ hour: 23, minute: 59, second: 59, millisecond: 999 }).epochMilliseconds)

  return (
    <TimePicker
      selected={selected?.start}
      onChange={date => {
        if (date && selected?.end && Temporal.PlainTime.compare(date, selected?.end) > 0) {
          setSelected({ start: selected?.end, end: selected?.end })
          onChange?.(selected?.end)
          return
        }
        setSelected({ start: date, end: selected?.end })
        onChange?.(date)
      }}
      minTime={todayBeginning}
      maxTime={endDate ?? todayEnd}
      {...props}
    />
  )
}

StartTimeInput.displayName = 'TimeRangePicker.StartTimeInput'

function EndTimeInput({ onChange, ...props }: Optional<TimePickerProps, 'onChange'>) {
  const { selected, setSelected } = useTimeRangeContext()

  const dateFmt = useDateFormatters()
  const startDate = dateFmt.toLegacyDate(selected?.start ?? undefined) ?? undefined
  const today = Temporal.Now.zonedDateTimeISO()
  const todayBeginning = new Date(today.startOfDay().epochMilliseconds)
  const todayEnd = new Date(today.with({ hour: 23, minute: 59, second: 59, millisecond: 999 }).epochMilliseconds)

  return (
    <TimePicker
      selected={selected?.end}
      onChange={date => {
        if (date && selected?.start && Temporal.PlainTime.compare(date, selected?.start) < 0) {
          setSelected({ start: selected?.start, end: selected?.start })
          onChange?.(selected?.start)
          return
        }
        setSelected({ start: selected?.start, end: date })
        onChange?.(date)
      }}
      minTime={startDate ?? todayBeginning}
      maxTime={todayEnd}
      {...props}
    />
  )
}

EndTimeInput.displayName = 'TimeRangePicker.EndTimeInput'

/// ///////////////////
// WeeklyRangePicker //
/// ///////////////////

export type WeeklyDatePickerProps = Omit<NonConfigurableDatePickerProps, 'startDate' | 'endDate'> & {
  onChange: (temporalValue: Temporal.PlainDate) => void
  startDate: Temporal.PlainDate
}

export const WeekRangePicker = forwardRef<WeeklyDatePickerProps, 'input'>(
  (
    {
      'data-testid': dataTestId = 'weekly-date-picker',
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      startDate,
      onChange,
      calendarStartDay,
      ...props
    },
    outerRef
  ) => {
    const dateFmt = useDateFormatters()
    const msg = useMsg()
    const { localeInfo } = useRegisterLazyLoadedLocale()
    const startOfWeekDay = calendarStartDay ?? localeInfo?.options?.weekStartsOn ?? 0

    const backOneWeek = () => onChange(startDate.subtract({ days: 7 }))
    const forwardOneWeek = () => onChange(startDate.add({ days: 7 }))

    const handleChange = (day: Date) => {
      const plainDate = dateFmt.fromLegacyDate.toPlainDate(day) || startDate
      // Calculate the first day of the week based on the calendar start day
      // The calendar start day is 0-indexed, with 0 being Sunday and 6 being Saturday
      // If not provided, the default is determined by the locale, and if that's not available, it defaults to Sunday
      const firstDayOfWeek = plainDate.subtract({ days: (plainDate.dayOfWeek - startOfWeekDay + 7) % 7 })
      onChange(firstDayOfWeek)
    }

    const innerRef = useRef<HTMLInputElement>(undefined)
    const ref = useMergeRefs(outerRef, innerRef)
    const [isFocused, setIsFocused] = useState(false)
    const weekEnd = startDate.add({ days: 6 })
    const weekRangeLabel = dateFmt.formatDateTimeRange(startDate, weekEnd, { date: true })
    const inputHint = msg('datepicker.weekly_picker.input_hint', { dateRange: weekRangeLabel })
    const hiddenInputLabel = ariaLabel ? `${ariaLabel}, ${inputHint}` : inputHint ?? undefined
    const groupLabel = ariaLabelledBy ? undefined : ariaLabel ?? msg('datepicker.weekly_picker.label')

    return (
      <Box width="fit-content" css={container}>
        <Flex
          data-testid="weekly_date_picker"
          className="weekly-picker"
          role="group"
          aria-labelledby={ariaLabelledBy}
          aria-label={groupLabel}
          minW={250}
          h={10}
          justifyContent="space-between"
          border="1px solid"
          borderColor="var(--vndly-color-container-border)"
          borderRadius="base"
          alignItems="stretch">
          <IconButton
            size="xs"
            icon={<ChevronLeft />}
            onClick={backOneWeek}
            aria-label={msg('datepicker.weekly_picker.previous_week')}
            variant="ghost"
            padding={1}
            h="full"
          />
          <BaseDatePicker
            selected={dateFmt.toLegacyDate(startDate)}
            showWeekPicker
            onChange={(date: Date | null) => {
              if (date == null) return
              handleChange(date)
            }}
            calendarStartDay={startOfWeekDay}
            onCalendarClose={() => innerRef.current?.focus()}
            customInput={
              <Flex
                borderRadius="base"
                boxShadow={isFocused ? 'middleOutline' : 'none'}
                px={3}
                h={10}
                cursor="pointer"
                alignItems="center"
                mt="-1px"
                _hover={{ bg: 'gray.200' }}>
                <VisuallyHiddenInput
                  ref={ref}
                  data-testid={dataTestId}
                  role="button"
                  aria-haspopup="dialog"
                  aria-label={hiddenInputLabel}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                />
                <Box aria-live="polite" aria-atomic="true">
                  <DateTimeRangeDisplay start={startDate} end={weekEnd} />
                </Box>
              </Flex>
            }
            inputProps={{ position: 'absolute', zIndex: -1, opacity: 0 }}
            {...props}
          />
          <IconButton
            size="xs"
            icon={<ChevronRight />}
            onClick={forwardOneWeek}
            aria-label={msg('datepicker.weekly_picker.next_week')}
            variant="ghost"
            padding={1}
            h="full"
          />
        </Flex>
      </Box>
    )
  }
)

WeekRangePicker.displayName = 'WeekRangePicker'
